import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wallet,
  Lock,
  TrendingUp,
  PackageCheck,
  Plus,
  ShoppingBag,
  Banknote,
  ChevronRight,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { AppLayout } from '../../components/layout/AppLayout';
import { EnableNotificationsBanner } from '../../components/EnableNotificationsBanner';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { earningsApi, EarningsSummary, TimelineEntry } from '../../api/earnings';
import {
  ordersApi,
  OrderStatus,
  OrderStats,
  ORDER_STATUS_LABEL,
  VendorOrder,
} from '../../api/orders';
import { formatNgn, formatDateTime } from '../../utils/format';

const STATUS_VARIANT: Record<
  OrderStatus,
  'success' | 'warning' | 'danger' | 'info' | 'neutral'
> = {
  placed: 'info',
  processing: 'info',
  shipped: 'info',
  out_for_delivery: 'info',
  delivered: 'success',
  completed: 'success',
  cancelled: 'danger',
  disputed: 'danger',
};

export const Dashboard: React.FC = () => {
  const { vendorUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [orders, setOrders] = useState<VendorOrder[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Pull everything the dashboard needs in parallel. Order counts
        // come from the dedicated /stats endpoint (exact at any scale);
        // the list call only feeds the recent-orders widget.
        const [summaryRes, statsRes, ordersRes, timelineRes] =
          await Promise.all([
            earningsApi.getSummary(),
            ordersApi.stats(),
            ordersApi.list({ limit: 6 }),
            earningsApi.getTimeline(1, 100),
          ]);
        if (cancelled) return;
        setSummary(summaryRes);
        setStats(statsRes);
        setOrders(ordersRes.items);
        setTimeline(timelineRes.items);
      } catch (err) {
        if (!cancelled) toast.error(err, 'Failed to load dashboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const pendingCount = stats?.pending ?? 0;
  const totalOrders = stats?.total ?? 0;

  // Bucket escrow-release events into the last 7 calendar days for the
  // earnings chart. Releases are the moment money becomes the vendor's,
  // so this reads as "what landed in my balance this week".
  const chart = useMemo(() => buildWeeklyReleases(timeline), [timeline]);

  const recentOrders = orders.slice(0, 6);

  return (
    <AppLayout>
      <div style={{ maxWidth: 1100 }}>
        <EnableNotificationsBanner />

        <div style={{ marginBottom: 24 }}>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 700,
              marginBottom: 4,
              color: 'var(--text-primary)',
            }}
          >
            Welcome back, {vendorUser?.vendor?.name ?? 'Vendor'}
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            Here's how your store is doing today.
          </p>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
            <Spinner size={28} />
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 16,
                marginBottom: 24,
              }}
            >
              <StatCard
                icon={<Wallet size={18} />}
                accent="var(--primary)"
                label="Payout balance"
                value={formatNgn(summary?.payoutBalance ?? 0)}
                hint="Available to withdraw"
              />
              <StatCard
                icon={<Lock size={18} />}
                accent="var(--yellow-900)"
                label="In escrow"
                value={formatNgn(summary?.lockedInEscrow ?? 0)}
                hint="Releases when buyers confirm"
              />
              <StatCard
                icon={<TrendingUp size={18} />}
                accent="var(--success)"
                label="Released (30 days)"
                value={formatNgn(summary?.releasedLast30Days ?? 0)}
                hint={`${formatNgn(summary?.releasedLast7Days ?? 0)} in last 7 days`}
              />
              <StatCard
                icon={<PackageCheck size={18} />}
                accent={pendingCount > 0 ? 'var(--error)' : 'var(--text-muted)'}
                label="Pending orders"
                value={String(pendingCount)}
                hint={
                  pendingCount > 0
                    ? 'Awaiting your action'
                    : 'You\'re all caught up'
                }
              />
            </div>

            {/* Quick actions */}
            <div
              style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                marginBottom: 24,
              }}
            >
              <QuickAction
                icon={<Plus size={16} />}
                label="Add product"
                onClick={() => navigate('/products')}
                primary
              />
              <QuickAction
                icon={<ShoppingBag size={16} />}
                label="View orders"
                onClick={() => navigate('/orders')}
              />
              <QuickAction
                icon={<Banknote size={16} />}
                label={
                  summary?.hasPayoutBank ? 'Request payout' : 'Set up payouts'
                }
                onClick={() => navigate('/earnings')}
              />
            </div>

            {/* Chart + recent orders */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
                gap: 16,
                alignItems: 'start',
              }}
            >
              <Card>
                <SectionTitle
                  title="Earnings this week"
                  subtitle="Escrow released to your balance, by day"
                />
                <WeeklyChart data={chart} />
              </Card>

              <Card padding={0}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '18px 20px 12px',
                  }}
                >
                  <SectionTitle
                    title="Recent orders"
                    subtitle={`${totalOrders} total`}
                    flush
                  />
                  <button
                    onClick={() => navigate('/orders')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--primary)',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    See all <ArrowRight size={14} />
                  </button>
                </div>

                {recentOrders.length === 0 ? (
                  <div
                    style={{
                      padding: '32px 20px',
                      textAlign: 'center',
                      color: 'var(--text-muted)',
                      fontSize: 14,
                    }}
                  >
                    No orders yet. They'll appear here as buyers check out.
                  </div>
                ) : (
                  <div>
                    {recentOrders.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => navigate('/orders')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px 20px',
                          background: 'transparent',
                          border: 'none',
                          borderTop: '1px solid var(--border)',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: 'var(--text-primary)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {o.reference}
                          </div>
                          <div
                            style={{ fontSize: 12, color: 'var(--text-muted)' }}
                          >
                            {o.shippingAddress?.fullName ?? '—'} ·{' '}
                            {formatDateTime(o.placedAt)}
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatNgn(o.total)}
                        </div>
                        <Badge variant={STATUS_VARIANT[o.status]}>
                          {ORDER_STATUS_LABEL[o.status]}
                        </Badge>
                        <ChevronRight size={16} color="var(--text-muted)" />
                      </button>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
};

/* ------------------------------- pieces -------------------------------- */

const StatCard: React.FC<{
  icon: React.ReactNode;
  accent: string;
  label: string;
  value: string;
  hint: string;
}> = ({ icon, accent, label, value, hint }) => (
  <Card>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        color: accent,
      }}
    >
      {icon}
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
    </div>
    <div
      style={{
        fontSize: 24,
        fontWeight: 700,
        color: 'var(--text-primary)',
        marginBottom: 4,
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{hint}</div>
  </Card>
);

const QuickAction: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  primary?: boolean;
}> = ({ icon, label, onClick, primary }) => (
  <button
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 18px',
      height: 42,
      borderRadius: 'var(--radius-md)',
      fontWeight: 600,
      fontSize: 14,
      cursor: 'pointer',
      background: primary ? 'var(--primary)' : 'var(--bg-card)',
      color: primary ? 'var(--text-inverse)' : 'var(--text-primary)',
      border: primary ? '1px solid var(--primary)' : '1px solid var(--border)',
    }}
  >
    {icon}
    {label}
  </button>
);

const SectionTitle: React.FC<{
  title: string;
  subtitle?: string;
  flush?: boolean;
}> = ({ title, subtitle, flush }) => (
  <div style={{ marginBottom: flush ? 0 : 16 }}>
    <div
      style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}
    >
      {title}
    </div>
    {subtitle ? (
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
        {subtitle}
      </div>
    ) : null}
  </div>
);

interface DayBucket {
  label: string;
  value: number;
}

/** Dependency-free responsive bar chart. Bars scale to the largest day;
 *  an all-zero week renders flat baselines rather than empty space. */
const WeeklyChart: React.FC<{ data: DayBucket[] }> = ({ data }) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 10,
          height: 160,
        }}
      >
        {data.map((d) => {
          const pct = (d.value / max) * 100;
          return (
            <div
              key={d.label}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                height: '100%',
                justifyContent: 'flex-end',
              }}
              title={`${d.label}: ${formatNgn(d.value)}`}
            >
              <div
                style={{
                  width: '100%',
                  maxWidth: 36,
                  height: `${Math.max(pct, 2)}%`,
                  background:
                    d.value > 0 ? 'var(--primary)' : 'var(--neutral-200)',
                  borderRadius: 'var(--radius-sm)',
                  transition: 'height 200ms ease',
                }}
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {d.label}
              </span>
            </div>
          );
        })}
      </div>
      <div
        style={{
          marginTop: 16,
          paddingTop: 12,
          borderTop: '1px solid var(--border)',
          fontSize: 13,
          color: 'var(--text-muted)',
        }}
      >
        <strong style={{ color: 'var(--text-primary)' }}>
          {formatNgn(total)}
        </strong>{' '}
        released in the last 7 days
      </div>
    </div>
  );
};

/** Build 7 day-buckets (oldest → today) of escrow releases from the
 *  earnings timeline. Payout entries are excluded — those are money
 *  leaving to the vendor's bank, not earnings coming in. */
function buildWeeklyReleases(timeline: TimelineEntry[]): DayBucket[] {
  const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const buckets: { date: Date; value: number }[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    buckets.push({ date: d, value: 0 });
  }

  for (const entry of timeline) {
    if (entry.kind !== 'release') continue;
    const at = new Date(entry.at);
    at.setHours(0, 0, 0, 0);
    const bucket = buckets.find((b) => b.date.getTime() === at.getTime());
    if (bucket) bucket.value += entry.amount;
  }

  return buckets.map((b) => ({
    label: DAY_LABELS[b.date.getDay()],
    value: b.value,
  }));
}
